import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  PencilIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { CaseParameters, SeedGenerationPayload } from '../types';

type PipelineStep = 'seeds' | 'edit' | 'review';

type SpeakerRoleOption = {
  id: string;
  label: string;
};

type SpeakerMode = 'family_caregiver' | 'patient_self';
type UiSpeakerRole = 'mother' | 'father' | 'guardian' | 'other_family';

const HIDDEN_DEFAULTS = {
  professionalLevel: 'proficient',
  copingStyle: 'coping_mixed',
  conflictStyle: 'conflict_collaborative',
} as const;

const SPEAKER_MODE_OPTIONS: Array<{ id: SpeakerMode; label: string; hint: string }> = [
  {
    id: 'family_caregiver',
    label: 'Family Caregiver',
    hint: 'A parent/guardian/family member speaks with the clinician.',
  },
  {
    id: 'patient_self',
    label: 'Patient Self',
    hint: 'The patient speaks directly with the clinician.',
  },
];

const FAMILY_SPEAKER_OPTIONS: SpeakerRoleOption[] = [
  { id: 'mother', label: 'Mother' },
  { id: 'father', label: 'Father' },
  { id: 'guardian', label: 'Guardian' },
  { id: 'other_family', label: 'Other Family Member' },
];

const mapUiSpeakerRoleToBackendRole = (role: string): string => {
  return role;
};

const parseFirstAgeNumber = (value: string): number | null => {
  const match = String(value || '').match(/\d+/);
  return match ? Number(match[0]) : null;
};

const CreateCasePipeline: React.FC = () => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<PipelineStep>('seeds');
  const [parameters, setParameters] = useState<CaseParameters | null>(null);

  const [selectedDisease, setSelectedDisease] = useState('');
  const [customDiseaseName, setCustomDiseaseName] = useState('');
  const [customDiseaseDescription, setCustomDiseaseDescription] = useState('');
  const [diseaseCategoryOverride, setDiseaseCategoryOverride] = useState('');

  const [seedPatientAge, setSeedPatientAge] = useState('');
  const [seedDiagnosis, setSeedDiagnosis] = useState('');
  const [seedRecentEvent, setSeedRecentEvent] = useState('');
  const [seedPrognosticStatus, setSeedPrognosticStatus] = useState('');
  const [speakerMode, setSpeakerMode] = useState<SpeakerMode>('family_caregiver');
  const [familySpeakerRole, setFamilySpeakerRole] = useState<UiSpeakerRole | ''>('');
  const [familyStructureText, setFamilyStructureText] = useState('');
  const [seedEmotionalContext, setSeedEmotionalContext] = useState('');
  const [seedKnownHopes, setSeedKnownHopes] = useState('');
  const [seedKnownWorries, setSeedKnownWorries] = useState('');
  const [showOptionalContextFields, setShowOptionalContextFields] = useState(false);
  const [showOptionalDiseaseRefinements, setShowOptionalDiseaseRefinements] = useState(false);

  const [objectives, setObjectives] = useState<string[]>(['']);

  const [editedCaseInfo, setEditedCaseInfo] = useState<any>(null);
  const [generatedDialogue, setGeneratedDialogue] = useState<any>(null);
  const [usedSeeds, setUsedSeeds] = useState<any>(null);
  const [dialogueInvalidated, setDialogueInvalidated] = useState(false);

  const [targetTurns, setTargetTurns] = useState(10);

  const [isGeneratingCase, setIsGeneratingCase] = useState(false);
  const [isGeneratingDialogue, setIsGeneratingDialogue] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    const loadParameters = async () => {
      try {
        const params = await apiService.getCaseParameters();
        setParameters(params);
      } catch (err) {
        console.error('Failed to load case parameters:', err);
        setError('Failed to load case parameters. Please refresh and try again.');
      }
    };

    loadParameters();
  }, []);

  const pruneEmptyValues = (input: Record<string, unknown>): Record<string, unknown> => {
    return Object.fromEntries(
      Object.entries(input).filter(([, value]) => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.trim().length > 0;
        return true;
      })
    );
  };

  const isPatientMode = speakerMode === 'patient_self';
  const parsedPatientAge = parseFirstAgeNumber(seedPatientAge);
  const isPatientModeAgeBlocked = isPatientMode && parsedPatientAge !== null && parsedPatientAge < 13;

  const buildFamilyStructureInput = (): string => {
    const selectedRoleLabel = FAMILY_SPEAKER_OPTIONS.find((option) => option.id === familySpeakerRole)?.label;
    const fragments: string[] = [];
    fragments.push(`Speaker mode: ${isPatientMode ? 'Patient Self' : 'Family Caregiver'}`);
    if (isPatientMode) {
      fragments.push('Primary speaker role: patient');
    } else if (selectedRoleLabel) {
      fragments.push(`Primary speaker relationship: ${selectedRoleLabel}`);
    }
    if (familyStructureText.trim()) fragments.push(`Family structure details: ${familyStructureText.trim()}`);
    return fragments.join('. ');
  };

  const buildStructuredSeedInputs = () => {
    return pruneEmptyValues({
      patient_age: seedPatientAge,
      diagnosis: seedDiagnosis,
      recent_event: seedRecentEvent,
      prognostic_status: seedPrognosticStatus,
      family_structure_input: buildFamilyStructureInput(),
      emotional_context: seedEmotionalContext,
      known_hopes: seedKnownHopes,
      known_worries: seedKnownWorries,
      speaker_mode: speakerMode,
    });
  };

  const normalizedObjectives = objectives.map((item) => item.trim()).filter(Boolean);

  const missingStepOneFields: string[] = [];
  if (!customDiseaseName.trim()) missingStepOneFields.push('Disease or medical condition');
  if (normalizedObjectives.length === 0) missingStepOneFields.push('At least one clinician objective');
  if (!seedPatientAge.trim()) missingStepOneFields.push('Patient age');
  if (!seedRecentEvent.trim()) missingStepOneFields.push('Recent clinical event');
  if (!seedPrognosticStatus.trim()) missingStepOneFields.push('Prognostic context');
  if (!isPatientMode && !familySpeakerRole.trim()) missingStepOneFields.push('Primary speaker role');
  if (isPatientModeAgeBlocked) missingStepOneFields.push('Patient Self mode is unavailable for age under 13');

  const isCaseInfoComplete = (caseInfo: any): boolean => {
    if (!caseInfo) return false;
    if (!caseInfo?.title?.trim()) return false;
    if (!caseInfo?.patient_profile?.name?.trim()) return false;
    if (!caseInfo?.patient_profile?.age?.trim()) return false;
    if (!caseInfo?.patient_profile?.diagnosis?.trim()) return false;
    if (!caseInfo?.scenario?.trim()) return false;
    if (!caseInfo?.family_statement?.trim()) return false;
    if (!Array.isArray(caseInfo?.clinician_objectives) || caseInfo.clinician_objectives.length === 0) return false;
    return !caseInfo.clinician_objectives.some((objective: string) => !String(objective || '').trim());
  };

  const missingStepTwoFields: string[] = [];
  if (!isCaseInfoComplete(editedCaseInfo)) {
    missingStepTwoFields.push('Complete required generated case information');
  }

  const canGenerateCaseInfo = missingStepOneFields.length === 0 && !isGeneratingCase;
  const canContinueToStep3 = missingStepTwoFields.length === 0;

  const addObjective = () => setObjectives((prev) => [...prev, '']);

  const removeObjective = (index: number) => {
    setObjectives((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      return next.length > 0 ? next : [''];
    });
  };

  const updateObjective = (index: number, value: string) => {
    setObjectives((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const invalidateDialogueIfPresent = () => {
    if (generatedDialogue) {
      setGeneratedDialogue(null);
      setDialogueInvalidated(true);
      setStatusMessage('');
    }
  };

  const updateCaseInfoWithInvalidation = (updater: (prev: any) => any) => {
    invalidateDialogueIfPresent();
    setEditedCaseInfo((prev: any) => updater(prev));
  };

  const handleGenerateCaseInfo = async () => {
    setError('');
    setDialogueInvalidated(false);

    if (missingStepOneFields.length > 0) {
      setError(`Please complete required fields: ${missingStepOneFields.join(', ')}`);
      return;
    }

    const uniqueObjectives = new Set(normalizedObjectives);
    if (uniqueObjectives.size !== normalizedObjectives.length) {
      setError('Please remove duplicate objectives before generating case info.');
      return;
    }

    let diseaseCategory = diseaseCategoryOverride || 'General';
    if (selectedDisease && parameters) {
      const selected = parameters.diseases.find((disease) => disease.id === selectedDisease);
      if (selected) {
        diseaseCategory = selected.category || diseaseCategory;
      }
    }

    const seeds: SeedGenerationPayload = {
      diseaseName: customDiseaseName.trim(),
      diseaseCategory,
      diseaseDescription: customDiseaseDescription.trim() || undefined,
      objectives: normalizedObjectives,
      guardian_count: isPatientMode ? undefined : 1,
      speaker_mode: speakerMode,
      primary: {
        role: isPatientMode ? 'patient' : mapUiSpeakerRoleToBackendRole(familySpeakerRole),
      },
      ...buildStructuredSeedInputs(),
      ...HIDDEN_DEFAULTS,
    };

    setIsGeneratingCase(true);
    setStatusMessage('Generating case info from structured inputs...');

    try {
      const response = await apiService.generateCaseInfoFromSeeds(seeds);
      if (!response?.success || !response?.caseInfo) {
        setError('Failed to generate case info.');
        return;
      }

      setEditedCaseInfo({ ...response.caseInfo });
      setGeneratedDialogue(null);
      setDialogueInvalidated(false);
      setUsedSeeds({ ...seeds, ...(response.seeds || {}) });
      setCurrentStep('edit');
      setStatusMessage('');
    } catch (err: any) {
      console.error('Case info generation failed:', err);
      setError(err?.response?.data?.error || 'Failed to generate case info. Please try again.');
      setStatusMessage('');
    } finally {
      setIsGeneratingCase(false);
    }
  };

  const handleGenerateDialogue = async () => {
    setError('');

    if (!isCaseInfoComplete(editedCaseInfo)) {
      setError('Please complete all required case info fields before generating dialogue.');
      return;
    }

    const mergedSeeds = {
      ...(usedSeeds || {}),
      guardian_count: isPatientMode ? undefined : 1,
      speaker_mode: speakerMode,
      primary: {
        role: isPatientMode ? 'patient' : mapUiSpeakerRoleToBackendRole(familySpeakerRole),
      },
      ...buildStructuredSeedInputs(),
      ...HIDDEN_DEFAULTS,
    };

    setIsGeneratingDialogue(true);
    setStatusMessage(`Generating dialogue with ${targetTurns} turns...`);

    try {
      const response = await apiService.generateDialogueFromCaseInfo(editedCaseInfo, mergedSeeds, targetTurns);
      if (!response?.success || !response?.dialogue) {
        setError('Failed to generate dialogue.');
        return;
      }

      setGeneratedDialogue(response.dialogue);
      setDialogueInvalidated(false);
      setUsedSeeds(mergedSeeds);
      setStatusMessage('');
    } catch (err: any) {
      console.error('Dialogue generation failed:', err);
      setError(err?.response?.data?.error || 'Failed to generate dialogue. Please try again.');
      setStatusMessage('');
    } finally {
      setIsGeneratingDialogue(false);
    }
  };

  const handleSaveCase = async () => {
    setError('');

    if (!editedCaseInfo || !generatedDialogue) {
      setError('Generate dialogue before saving.');
      return;
    }

    setIsSaving(true);

    try {
      const response = await apiService.saveReviewedCase(editedCaseInfo, generatedDialogue, usedSeeds || {});
      if (response?.success) {
        navigate('/cases');
      } else {
        setError('Failed to save case.');
      }
    } catch (err: any) {
      console.error('Save failed:', err);
      setError(err?.response?.data?.error || 'Failed to save case.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackClick = () => {
    if (currentStep === 'seeds') {
      navigate('/cases');
      return;
    }
    if (currentStep === 'edit') {
      setCurrentStep('seeds');
      return;
    }
    setCurrentStep('edit');
  };

  if (!parameters) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">Loading parameters...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={handleBackClick}
            className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            {currentStep === 'seeds' && 'Back to Cases'}
            {currentStep === 'edit' && 'Back to Step 1'}
            {currentStep === 'review' && 'Back to Step 2'}
          </button>
        </div>

        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep === 'seeds' ? 'bg-blue-600 text-white' : 'bg-green-500 text-white'}`}>
              {currentStep === 'seeds' ? '1' : '✓'}
            </div>
            <div className={`w-24 h-1 ${currentStep !== 'seeds' ? 'bg-green-500' : 'bg-gray-300'}`} />
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep === 'edit' ? 'bg-blue-600 text-white' : currentStep === 'review' ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
              {currentStep === 'review' ? '✓' : '2'}
            </div>
            <div className={`w-24 h-1 ${currentStep === 'review' ? 'bg-green-500' : 'bg-gray-300'}`} />
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep === 'review' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
              3
            </div>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-900">
          {currentStep === 'seeds' && 'Step 1: Structured Inputs and Case Info'}
          {currentStep === 'edit' && 'Step 2: Review and Edit Case Info'}
          {currentStep === 'review' && 'Step 3: Generate Dialogue and Save'}
        </h1>
        <p className="mt-2 text-gray-600">
          {currentStep === 'seeds' && 'Provide clear context in plain language, then generate case info.'}
          {currentStep === 'edit' && 'Review and edit generated case info before generating dialogue.'}
          {currentStep === 'review' && 'Generate dialogue from current Step 2 case info, then save.'}
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {statusMessage && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3" />
            <p className="text-blue-800 font-medium">{statusMessage}</p>
          </div>
        </div>
      )}

      {dialogueInvalidated && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-md p-4">
          <p className="text-amber-900 font-medium">Case info changed. Regenerate dialogue before save.</p>
        </div>
      )}

      {currentStep === 'seeds' && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-sm text-blue-900">
            <span className="font-semibold">Required:</span> Disease/condition, speaker mode, at least one objective, Patient age, Recent clinical event, and Prognostic context.
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Disease / Medical Condition</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select from Library</label>
                <select
                  value={selectedDisease}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedDisease(value);
                    if (!value) {
                      setDiseaseCategoryOverride('');
                      return;
                    }
                    const disease = parameters.diseases.find((item) => item.id === value);
                    if (disease) {
                      setCustomDiseaseName(disease.name);
                      setCustomDiseaseDescription(disease.description || '');
                      setDiseaseCategoryOverride(disease.category || '');
                      setSeedDiagnosis(disease.name);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Select a disease --</option>
                  {parameters.diseases.map((disease) => (
                    <option key={disease.id} value={disease.id}>
                      {disease.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Disease Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={customDiseaseName}
                  onChange={(e) => {
                    setCustomDiseaseName(e.target.value);
                    if (selectedDisease) {
                      setSelectedDisease('');
                      setDiseaseCategoryOverride('');
                    }
                  }}
                  placeholder="e.g., Acute lymphoblastic leukemia"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="pt-1">
                <button
                  type="button"
                  onClick={() => setShowOptionalDiseaseRefinements((prev) => !prev)}
                  className="text-sm text-blue-700 hover:text-blue-900"
                >
                  {showOptionalDiseaseRefinements ? 'Hide optional disease refinements' : 'Show optional disease refinements'}
                </button>
              </div>

              {showOptionalDiseaseRefinements && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Disease Details (Optional)</label>
                    <textarea
                      value={customDiseaseDescription}
                      onChange={(e) => setCustomDiseaseDescription(e.target.value)}
                      rows={2}
                      placeholder="Additional condition details that should influence realism"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Diagnosis Details (optional refinement)</label>
                    <input
                      type="text"
                      value={seedDiagnosis}
                      onChange={(e) => setSeedDiagnosis(e.target.value)}
                      placeholder="Auto-filled from disease; edit if needed"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Clinician Objectives</h2>
            <p className="text-xs text-gray-500 mb-3">At least one objective is required.</p>

            <div className="space-y-3">
              {objectives.map((objective, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={objective}
                    onChange={(e) => updateObjective(index, e.target.value)}
                    placeholder="e.g., Explore hopes and worries before recommendation"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {objectives.length > 1 && (
                    <button onClick={() => removeObjective(index)} className="px-3 py-2 text-red-600 hover:text-red-800">
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button onClick={addObjective} className="mt-3 flex items-center text-blue-600 hover:text-blue-800">
              <PlusIcon className="h-5 w-5 mr-1" />
              Add Objective
            </button>

            {parameters.objectives.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-2">Click to add from library:</p>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-3">
                  <div className="flex flex-wrap gap-2">
                    {parameters.objectives.map((obj) => {
                      const isAdded = objectives.includes(obj.name);
                      return (
                        <button
                          key={obj.id}
                          onClick={() => {
                            if (isAdded) return;
                            const emptyIndex = objectives.findIndex((item) => !item.trim());
                            if (emptyIndex !== -1) {
                              updateObjective(emptyIndex, obj.name);
                            } else {
                              setObjectives((prev) => [...prev, obj.name]);
                            }
                          }}
                          disabled={isAdded}
                          className={`px-3 py-1 text-sm rounded-full ${
                            isAdded ? 'bg-green-100 text-green-700 cursor-default' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                          }`}
                          title={obj.description || obj.name}
                        >
                          {isAdded ? '✓ ' : '+ '}
                          {obj.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Goals and Values Structured Inputs</h2>
            <p className="text-xs text-gray-500 mb-4">Recent clinical event and prognostic context drive the quality of case and dialogue output.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient Age <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={seedPatientAge}
                  onChange={(e) => setSeedPatientAge(e.target.value)}
                  placeholder="e.g., 8 years old"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recent Clinical Event <span className="text-red-600">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-1">What changed recently?</p>
                <textarea
                  value={seedRecentEvent}
                  onChange={(e) => setSeedRecentEvent(e.target.value)}
                  rows={2}
                  placeholder="e.g., MRI today shows new progression despite current treatment"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prognostic Context <span className="text-red-600">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-1">What does this change mean for likely risk/trajectory (without absolute time unless known)?</p>
                <textarea
                  value={seedPrognosticStatus}
                  onChange={(e) => setSeedPrognosticStatus(e.target.value)}
                  rows={2}
                  placeholder="e.g., If progression continues, short-term deterioration risk is high"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Speaker Mode <span className="text-red-600">*</span></label>
                <div className="space-y-2 mb-3">
                  {SPEAKER_MODE_OPTIONS.map((option) => (
                    <label key={option.id} className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="speaker_mode"
                        value={option.id}
                        checked={speakerMode === option.id}
                        onChange={(e) => setSpeakerMode(e.target.value as SpeakerMode)}
                        className="mt-1"
                      />
                      <span className="text-sm">
                        <span className="font-medium text-gray-900">{option.label}</span>
                        <span className="block text-xs text-gray-500">{option.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
                {isPatientMode ? (
                  <div className="mb-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    Primary Speaker Role: <span className="font-semibold">Patient</span>
                  </div>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary Speaker Role <span className="text-red-600">*</span></label>
                    <p className="text-xs text-gray-500 mb-1">Pick who is speaking with the clinician.</p>
                    <select
                      value={familySpeakerRole}
                      onChange={(e) => setFamilySpeakerRole(e.target.value as UiSpeakerRole | '')}
                      className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select relationship</option>
                      {FAMILY_SPEAKER_OPTIONS.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <label className="block text-xs font-medium text-gray-600 mb-1">Family / Support Context (free text, optional)</label>
                <textarea
                  value={familyStructureText}
                  onChange={(e) => setFamilyStructureText(e.target.value)}
                  rows={2}
                  placeholder="Describe support context, e.g., lives with mother, aunt is primary caregiver, no nearby family"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {isPatientModeAgeBlocked && (
                  <p className="mt-2 text-xs text-red-700">
                    Patient Self mode is unavailable for age under 13; switch to Family Caregiver.
                  </p>
                )}
              </div>

            </div>

            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowOptionalContextFields((prev) => !prev)}
                className="text-sm text-blue-700 hover:text-blue-900"
              >
                {showOptionalContextFields ? 'Hide optional context fields' : 'Show optional context fields'}
              </button>
            </div>

            {showOptionalContextFields && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Emotional Context</label>
                  <p className="text-xs text-gray-500 mb-1">Optional, but improves emotional realism and family statement quality.</p>
                  <input
                    type="text"
                    value={seedEmotionalContext}
                    onChange={(e) => setSeedEmotionalContext(e.target.value)}
                    placeholder="e.g., Parent is exhausted, scared, and trying to stay composed"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Known Hopes</label>
                  <p className="text-xs text-gray-500 mb-1">Optional, but helps generate values-aligned parent language.</p>
                  <textarea
                    value={seedKnownHopes}
                    onChange={(e) => setSeedKnownHopes(e.target.value)}
                    rows={2}
                    placeholder="e.g., Keep the child comfortable and awake enough to talk with family"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Known Worries</label>
                  <p className="text-xs text-gray-500 mb-1">Optional, but helps realism and emotional escalation logic.</p>
                  <textarea
                    value={seedKnownWorries}
                    onChange={(e) => setSeedKnownWorries(e.target.value)}
                    rows={2}
                    placeholder="e.g., Fear of suffering and fear of making the wrong decision"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            {missingStepOneFields.length > 0 && (
              <div className="mr-4 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800">
                <div className="font-semibold">Missing required fields:</div>
                <div>{missingStepOneFields.join(', ')}</div>
              </div>
            )}
            <button
              onClick={handleGenerateCaseInfo}
              disabled={!canGenerateCaseInfo}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SparklesIcon className="h-5 w-5 mr-2" />
              {isGeneratingCase ? 'Generating Case Info...' : 'Generate Case Info'}
            </button>
          </div>
        </div>
      )}

      {currentStep === 'edit' && editedCaseInfo && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Review and Edit Generated Case Info</h2>
            <p className="text-sm text-gray-600 mb-4">Any edit here invalidates Step 3 dialogue and requires regeneration.</p>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Case ID</label>
                  <input
                    type="text"
                    value={editedCaseInfo.case_id || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={editedCaseInfo.title || ''}
                    onChange={(e) => updateCaseInfoWithInvalidation((prev) => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name</label>
                  <input
                    type="text"
                    value={editedCaseInfo?.patient_profile?.name || ''}
                    onChange={(e) =>
                      updateCaseInfoWithInvalidation((prev) => ({
                        ...prev,
                        patient_profile: { ...prev.patient_profile, name: e.target.value },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                  <input
                    type="text"
                    value={editedCaseInfo?.patient_profile?.age || ''}
                    onChange={(e) =>
                      updateCaseInfoWithInvalidation((prev) => ({
                        ...prev,
                        patient_profile: { ...prev.patient_profile, age: e.target.value },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight</label>
                  <input
                    type="text"
                    value={editedCaseInfo?.patient_profile?.weight || ''}
                    onChange={(e) =>
                      updateCaseInfoWithInvalidation((prev) => ({
                        ...prev,
                        patient_profile: { ...prev.patient_profile, weight: e.target.value },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Diagnosis</label>
                  <input
                    type="text"
                    value={editedCaseInfo?.patient_profile?.diagnosis || ''}
                    onChange={(e) =>
                      updateCaseInfoWithInvalidation((prev) => ({
                        ...prev,
                        patient_profile: { ...prev.patient_profile, diagnosis: e.target.value },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scenario</label>
                <textarea
                  value={editedCaseInfo.scenario || ''}
                  onChange={(e) => updateCaseInfoWithInvalidation((prev) => ({ ...prev, scenario: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isPatientMode ? 'Primary Speaker Statement' : 'Family Statement'}
                </label>
                <textarea
                  value={editedCaseInfo.family_statement || ''}
                  onChange={(e) => updateCaseInfoWithInvalidation((prev) => ({ ...prev, family_statement: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Clinician Objectives</label>
                {(editedCaseInfo.clinician_objectives || []).map((objective: string, idx: number) => (
                  <input
                    key={idx}
                    type="text"
                    value={objective}
                    onChange={(e) => {
                      updateCaseInfoWithInvalidation((prev) => {
                        const nextObjectives = [...prev.clinician_objectives];
                        nextObjectives[idx] = e.target.value;
                        return { ...prev, clinician_objectives: nextObjectives };
                      });
                    }}
                    className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setCurrentStep('seeds')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Back to Step 1
            </button>

            <div className="flex items-center">
              {missingStepTwoFields.length > 0 && (
                <div className="mr-4 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800">
                  <div className="font-semibold">Missing required fields:</div>
                  <div>{missingStepTwoFields.join(', ')}</div>
                </div>
              )}
              <button
                onClick={() => setCurrentStep('review')}
                disabled={!canContinueToStep3}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Step 3
              </button>
            </div>
          </div>
        </div>
      )}

      {currentStep === 'review' && editedCaseInfo && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Dialogue Generation</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Dialogue Turns: <span className="text-blue-600 font-bold">{targetTurns}</span>
              </label>
              <input
                type="range"
                min="6"
                max="20"
                value={targetTurns}
                onChange={(e) => setTargetTurns(Number(e.target.value))}
                disabled={isGeneratingDialogue}
                className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>6 (Brief)</span>
                <span>13 (Standard)</span>
                <span>20 (Extended)</span>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={handleGenerateDialogue}
                disabled={isGeneratingDialogue}
                className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <SparklesIcon className="h-5 w-5 mr-2" />
                {isGeneratingDialogue ? 'Generating Dialogue...' : generatedDialogue ? 'Regenerate Dialogue' : 'Generate Dialogue'}
              </button>
            </div>
          </div>

          {generatedDialogue && (
            <>
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Case Information</h2>
                  <button onClick={() => setCurrentStep('edit')} className="flex items-center text-blue-600 hover:text-blue-800 text-sm">
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit Case Info
                  </button>
                </div>

                <div className="space-y-3 text-sm">
                  <div><strong>Case ID:</strong> {editedCaseInfo.case_id}</div>
                  <div><strong>Title:</strong> {editedCaseInfo.title}</div>
                  <div><strong>Patient:</strong> {editedCaseInfo.patient_profile?.name}, {editedCaseInfo.patient_profile?.age}</div>
                  <div><strong>Diagnosis:</strong> {editedCaseInfo.patient_profile?.diagnosis}</div>
                  <div><strong>Scenario:</strong> {editedCaseInfo.scenario}</div>
                  <div><strong>{isPatientMode ? 'Primary Speaker Statement' : 'Family Statement'}:</strong> {editedCaseInfo.family_statement}</div>
                  <div>
                    <strong>Objectives:</strong>
                    <ul className="list-disc list-inside ml-4 mt-1">
                      {(editedCaseInfo.clinician_objectives || []).map((objective: string, idx: number) => (
                        <li key={idx}>{objective}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Dialogue ({generatedDialogue.dialogue?.length || 0} turns)
                </h2>

                <div className="text-sm text-gray-600 mb-4">
                  <strong>Setting:</strong> {generatedDialogue.setting || 'Not specified'}
                  <strong className="ml-2">Participants:</strong>{' '}
                  {generatedDialogue.participants?.map((participant: any) => `${participant.name} (${participant.role})`).join(', ')}
                </div>

                <div className="space-y-3">
                  {generatedDialogue.dialogue?.map((turn: any) => {
                    const speaker = String(turn.speaker || '').toLowerCase();
                    const isDoctor = speaker.includes('dr') || speaker.includes('doctor') || speaker.includes('clinician');
                    return (
                      <div key={turn.turn} className={`flex ${isDoctor ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-3xl px-4 py-3 rounded-lg ${isDoctor ? 'bg-blue-100 text-blue-900' : 'bg-gray-100 text-gray-900'}`}>
                          <div className="text-xs font-semibold mb-1">Turn {turn.turn}: {turn.speaker}</div>
                          <div className="text-sm whitespace-pre-wrap">{turn.line}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setCurrentStep('edit')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Back to Step 2
                </button>
                <button
                  onClick={handleSaveCase}
                  disabled={isSaving || !generatedDialogue}
                  className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  {isSaving ? 'Saving...' : 'Save Case'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CreateCasePipeline;
